/**
 * WaMessageThread — Renderizado del hilo de mensajes WhatsApp.
 *
 * Muestra burbujas de chat estilo WhatsApp con soporte para:
 * - Texto, imágenes, video, audio, documentos, stickers, ubicación
 * - Notas internas (fondo amarillo)
 * - Mensajes de sistema (centrados)
 * - Indicadores de entrega (✓, ✓✓, ✓✓ azul)
 * - Reacciones con emoji (hover → picker → detalle)
 * - Separadores de fecha
 */

import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from 'react';
import {
  ActionList,
  Box,
  Button,
  Icon,
  InlineStack,
  Popover,
  Spinner,
  Text,
} from '@/lib/polaris';
import { supabase } from '@/lib/supabase';
import {
  ChatIcon,
  PinFilledIcon,
  PinIcon,
  ArrowDownIcon,
  ChevronDownIcon,
} from '@/lib/polaris-icons';
import { WaImageLightbox, WaPinModal, WaReactionDetailModal } from './WaModals';
import { WaOrderDetailsModal } from './WaOrderDetailsModal';
import { waMediaPreview } from './wa-helpers';
import { WaMessageRow } from './WaMessageRow';
import { buildGroupMentionMap } from './waGroupMentions';
import { WaImageAlbum, canExtendAlbum, isAlbumCandidate } from './WaImageAlbum';
import { WaAvatar } from './WaAvatar';
import { groupParticipantColor, groupParticipantInitials } from './_helpers/messageRowHelpers';
import { deriveParticipantAvatarKey } from '@/hooks/useGroupParticipantAvatars';
import { waChatBgStyle } from './wa-chat-bg';
import { DensidadHiloProvider, useDensidadPorAncho } from './waThreadDensity';
import { useStaffDirectory } from '@/hooks/useStaffDirectory';
import { useWaAuditEvents } from '@/hooks/useWaAuditEvents';
import { useGroupParticipantAvatars } from '@/hooks/useGroupParticipantAvatars';
import { WaTimelinePill } from './WaTimelinePill';
import { AppConfirmModal } from '@/components/admin/AppConfirmModal';

// ─── Types ───

export interface MessageReaction {
  emoji: string;
  from: string;
  from_me: boolean;
  timestamp: number;
}

export interface WaMessage {
  /**
   * De dónde salió el mensaje: 'humano' · 'agente_ia' · 'campana_calidad' · 'campana_venta' ·
   * 'aviso_entrega' · 'programado'. `null` en los mensajes anteriores al campo — ahí no se muestra
   * etiqueta, que es más honesto que inventarle un origen.
   */
  origen?: string | null;
  id: string;
  direction: string;
  sender_type: string;
  sender_id: string | null;
  body: string | null;
  msg_type: string;
  media_url: string | null;
  media_caption: string | null;
  media_filename: string | null;
  media_status: string | null;
  /** Data URI base64 del thumbnail inline (de Baileys jpegThumbnail).
   *  Permite render inmediato del preview mientras llega media_url full. */
  media_thumbnail?: string | null;
  delivery_status: string;
  is_internal_note: boolean;
  created_at: string;
  reactions: MessageReaction[];
  wa_message_id: string | null;
  error_message?: string | null;
  audio_duration?: number;
  transcription?: string | null;
  transcription_status?: string | null;
  transcription_lang?: string | null;
  quoted_message_id?: string | null;
  quoted_message?: {
    id: string;
    body: string | null;
    msg_type: string;
    direction: string;
    media_url?: string | null;
  } | null;
  /** Para mensajes inbound de grupos: phone E.164 del participant (sender real). */
  group_participant_phone?: string | null;
  /** Para mensajes inbound de grupos: pushName del participant. Render como header del bubble. */
  group_participant_name?: string | null;
  /** JID original (`@s.whatsapp.net` o `@lid`). Resuelve avatar en privacy mode. */
  group_participant_jid?: string | null;
  /** Feature 3 (Edit message del fork): TRUE si fue editado. Render "(editado)". */
  is_edited?: boolean;
  /** Timestamp del último edit. NULL si nunca editado. */
  edited_at?: string | null;
  /** Timestamp de la revocación "Eliminar para todos". Cuando está seteado,
   *  el bubble renderiza "🚫 Este mensaje fue eliminado" en lugar del contenido. */
  deleted_at?: string | null;
  /** Used internally para optimistic UI / DB persisted flag. */
  is_starred?: boolean;
  /**
   * Anuncio click-to-WhatsApp de Meta que originó el mensaje. Se pinta como
   * tarjeta arriba del texto, igual que la ve el cliente en su celular.
   */
  ad_referral?: {
    sourceId?: string | null;
    title?: string | null;
    body?: string | null;
    sourceUrl?: string | null;
    /** 'fb' | 'ig' */
    sourceType?: string | null;
    /** 'image' | 'video' — el video llega como miniatura (still), no reproducible. */
    mediaType?: string | null;
    /** Click-to-WhatsApp Click ID (atribución con Meta). */
    ctwaClid?: string | null;
    thumbnail?: string | null;
  } | null;
  /** Feature 5c-A/B: payload del orderMessage cuando msg_type='order'.
   *  Contiene orderId, token, total, currency, itemCount, message del cliente,
   *  orderTitle, sellerJid, status. Y opcionalmente `items` cuando el
   *  pre-fetch (Feature 5c-B) ya consultó getOrderDetails y persistió. */
  order_info?: {
    orderId?: string;
    token?: string;
    totalAmount1000?: number;
    currency?: string;
    itemCount?: number;
    message?: string;
    orderTitle?: string;
    sellerJid?: string;
    status?: number;
    /** Items pre-fetchados: nombre + imagen + precio + qty. Si está
     *  poblado, la card y el modal lo usan directo (sin fetch). */
    items?: Array<{
      id?: string;
      retailerId?: string;
      name?: string;
      quantity?: number;
      price?: number | string;
      currency?: string;
      imageUrl?: string;
      productImageCount?: number;
    }>;
    items_fetched_at?: string;
  } | null;
}

// ─── Props ───

export interface WaMessageThreadHandle {
  scrollToMessage: (messageId: string) => void;
  hasMessage: (messageId: string) => boolean;
}

interface Props {
  messages: WaMessage[];
  loading: boolean;
  onSendReaction: (msgId: string, emoji: string) => void;
  onRetryMessage?: (msgId: string) => void;
  onForwardMessage?: (msg: WaMessage) => void;
  onForwardMessages?: (msgs: WaMessage[]) => void;
  onDeleteMessage?: (msgId: string) => void;
  onReplyMessage?: (msg: WaMessage) => void;
  onEditMessage?: (msg: WaMessage) => void;
  onShowMessageInfo?: (msg: WaMessage) => void;
  onPinMessage?: (msg: WaMessage, duration: string) => void;
  pinnedMessage?: WaMessage | null;
  onUnpinMessage?: () => void;
  onStarMessage?: (msgId: string) => void;
  /** Feature 5c-B: datos del contacto seleccionado para mostrar en el modal
   *  de order details SIN fetch (memoria primero). */
  contactName?: string | null;
  contactPhone?: string;
  /** Feature 5c-B: callback para crear orden COD pre-poblada con los items
   *  del orderMessage. El padre maneja abrir WaQuickOrderModal con initialItems. */
  onCreateOrderFromCatalog?: (params: {
    items: Array<{
      id?: string;
      retailerId?: string;
      name?: string;
      quantity?: number;
      price?: number | string;
      currency?: string;
      imageUrl?: string;
    }>;
    totalAmount1000?: number;
    currency?: string;
  }) => void;
  /** Feature 5c-B: enviar respuesta de texto rápida (la usa el modal de
   *  order details para "Responder al cliente" con plantilla). */
  onSendQuickReply?: (text: string) => void;
  contactPhotoUrl?: string | null;
  currentUserAvatarUrl?: string | null;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  hasOlderMessages?: boolean;
  loadingOlder?: boolean;
  onLoadOlder?: () => void;
  /** Usado para cargar eventos del timeline (asignación, tags, etc.). */
  conversationId?: string | null;
  /** Necesario para resolver emails vía RPC cuando un user no tiene profile. */
  tenantId?: string | null;
  /** Staff del tenant — usado para highlightear @nombres dentro de notas internas. */
  mentionableNames?: string[];
}

// ─── Component ───

export const WaMessageThread = forwardRef<WaMessageThreadHandle, Props>(function WaMessageThread({ messages, loading, onSendReaction, onRetryMessage, onForwardMessage, onForwardMessages, onDeleteMessage, onReplyMessage, onEditMessage, onShowMessageInfo, onPinMessage, pinnedMessage, onUnpinMessage, onStarMessage, contactName, contactPhone, contactPhotoUrl, onCreateOrderFromCatalog, onSendQuickReply, currentUserAvatarUrl, messagesEndRef, hasOlderMessages, loadingOlder, onLoadOlder, conversationId, tenantId, mentionableNames }, ref) {
  const [hoveredMsgId, setHoveredMsgId] = useState<string | null>(null);
  const [reactionPickerMsgId, setReactionPickerMsgId] = useState<string | null>(null);
  const [reactionDetailMsgId, setReactionDetailMsgId] = useState<string | null>(null);
  const [highlightedMsgId, setHighlightedMsgId] = useState<string | null>(null);
  // Lightbox: guardamos IDs (no el snapshot de WaMessage[]) para que el
  // estado del msg (is_starred, pin status, reactions, media_url) refleje
  // siempre el estado FRESCO del prop `messages`. Si guardáramos los msgs
  // directamente, al destacar/fijar el ícono no se actualizaría hasta
  // reabrir el lightbox.
  const [lightboxData, setLightboxData] = useState<{
    msgIds: string[];
    index: number;
  } | null>(null);

  // Feature 5c-B: modal de detalle de orderMessage. Singleton para que
  // virtualización de rows no lo desmonte al hacer scroll. Si el pre-fetch
  // del webhook ya pobló order_info.items y el realtime los trajo al
  // cliente, los pasamos acá para que el modal los renderice instantáneo
  // sin tocar el edge function.
  const [orderDetailsCtx, setOrderDetailsCtx] = useState<{
    orderId: string;
    token: string;
    totalAmount1000?: number;
    currency?: string;
    prefetchedItems?: Array<Record<string, unknown>>;
    orderDate: string;
  } | null>(null);

  // Avatares de participantes de grupos: SELECT batch + realtime UPDATE
  // cuando el background fetch del webhook completa. Map: phone → url.
  const groupParticipantAvatars = useGroupParticipantAvatars(tenantId, messages);

  // Mapa userpart→nombre para resolver @menciones de grupo (queda vacío en
  // chats 1:1 porque no hay group_participant_* → auto-gating). Dep en
  // `messages.length` (no `messages`): los participantes nuevos siempre
  // llegan con mensajes nuevos, y un edit/delete in-place NO cambia las
  // menciones. Evita recalcular + re-render de TODAS las rows en cada
  // cambio de referencia del array (causa de flicker en grupos).
   
  const groupMentionMap = useMemo(
    () => buildGroupMentionMap(messages),
    [messages.length],
  );

  // Abre el lightbox con TODAS las imágenes/videos del thread (estilo
  // WhatsApp Web). El carrusel muestra el chat completo en orden, con
  // la imagen clickeada como la activa al inicio. Esto aplica tanto a
  // clicks en imágenes sueltas como en tiles de álbumes.
  const openLightboxAt = useCallback((msgId: string) => {
    const allMediaIds = messages
      .filter((m) => (m.msg_type === 'image' || m.msg_type === 'video') && (m.media_url || m.media_thumbnail))
      .map((m) => m.id);
    const index = allMediaIds.indexOf(msgId);
    if (index < 0) return;
    setLightboxData({ msgIds: allMediaIds, index });
  }, [messages]);

  const openLightboxForMessages = useCallback((msgs: WaMessage[], startIndex: number) => {
    const target = msgs[Math.max(0, Math.min(startIndex, msgs.length - 1))];
    if (target) openLightboxAt(target.id);
  }, [openLightboxAt]);

  const openLightboxSingle = useCallback((url: string, msg?: WaMessage) => {
    if (!url || !msg) return;
    openLightboxAt(msg.id);
  }, [openLightboxAt]);

  // Resuelve los msgs vivos del prop por ID — mantiene `is_starred`/pin
  // actualizados en el lightbox sin reabrir.
  const lightboxMsgs = useMemo(() => {
    if (!lightboxData) return null;
    const byId = new Map(messages.map((m) => [m.id, m]));
    return lightboxData.msgIds.map((id) => byId.get(id)).filter((m): m is WaMessage => !!m);
  }, [lightboxData, messages]);
  const [contextMenuMsgId, setContextMenuMsgId] = useState<string | null>(null);
  const [contextMenuOpenUp, setContextMenuOpenUp] = useState(false);
  const [pinModalMsg, setPinModalMsg] = useState<WaMessage | null>(null);
  const [showPinMenu, setShowPinMenu] = useState(false);
  // Retry media download per-message
  const [retryingMsgIds, setRetryingMsgIds] = useState<Set<string>>(new Set());
  const [failedRetryMsgIds, setFailedRetryMsgIds] = useState<Set<string>>(new Set());
  // Modal "No se envió tu mensaje" (patrón WhatsApp Web). Se abre al hacer
  // click en el icono rojo del mensaje fallido.
  const [retryTarget, setRetryTarget] = useState<WaMessage | null>(null);

  // Resolve agent names for outbound messages (Chatwoot-style header).
  // Solo mostramos el nombre cuando hay >=2 agentes distintos en el hilo,
  // para evitar ruido en conversaciones con un único asesor.
  // Dep en messages.length (no messages): un edit/delete in-place no
  // cambia el set de agentes; solo un mensaje nuevo puede. Evita
  // recálculo + cascada de re-renders en cada cambio de ref del array
  // (mismo criterio que groupMentionMap).
   
  const outboundAgentIds = useMemo(
    () =>
      messages
        .filter((m) => m.direction === 'outbound' && m.sender_type === 'agent' && m.sender_id)
        .map((m) => m.sender_id as string),
    [messages.length],
  );
  const uniqueAgentCount = useMemo(
    () => new Set(outboundAgentIds).size,
    [outboundAgentIds],
  );

  const { events: auditEvents } = useWaAuditEvents(conversationId ?? null);

  // IDs combinados: agentes mensaje + actores de events. Un único query a profiles.
  const staffIds = useMemo(() => {
    const ids = new Set<string>(outboundAgentIds);
    for (const e of auditEvents) {
      if (e.actor_id) ids.add(e.actor_id);
      const d = e.details as Record<string, unknown>;
      const toId = typeof d.to_agent_id === 'string' ? d.to_agent_id : null;
      const fromId = typeof d.from_agent_id === 'string' ? d.from_agent_id : null;
      if (toId) ids.add(toId);
      if (fromId) ids.add(fromId);
    }
    return Array.from(ids);
  }, [outboundAgentIds, auditEvents]);

  const { resolve: resolveAgentName } = useStaffDirectory(staffIds, tenantId);

  // Agrupa eventos por mensaje que los precede (patrón Chatwoot: pill antes del msg siguiente).
  const eventGroups = useMemo(() => {
    const groups = new Map<string, typeof auditEvents>();
    const tail: typeof auditEvents = [];
    let ei = 0;
    const sorted = auditEvents; // ya viene ordenado asc por created_at
    for (const m of messages) {
      const bucket: typeof auditEvents = [];
      const mTime = new Date(m.created_at).getTime();
      while (ei < sorted.length && new Date(sorted[ei].created_at).getTime() < mTime) {
        bucket.push(sorted[ei++]);
      }
      if (bucket.length > 0) groups.set(m.id, bucket);
    }
    while (ei < sorted.length) tail.push(sorted[ei++]);
    return { groups, tail };
  }, [messages, auditEvents]);

  const retryMediaDownload = useCallback(async (messageId: string) => {
    setRetryingMsgIds((prev) => new Set(prev).add(messageId));
    setFailedRetryMsgIds((prev) => { const n = new Set(prev); n.delete(messageId); return n; });
    try {
      const { data, error } = await supabase.functions.invoke('whatsapp-retry-media', {
        body: { message_id: messageId },
      });
      if (error || !data?.success) {
        setFailedRetryMsgIds((prev) => new Set(prev).add(messageId));
      }
    } catch {
      setFailedRetryMsgIds((prev) => new Set(prev).add(messageId));
    } finally {
      setRetryingMsgIds((prev) => { const n = new Set(prev); n.delete(messageId); return n; });
    }
  }, []);

  // Multi-select forward mode
  const [selectMode, setSelectMode] = useState(false);
  const [selectedMsgIds, setSelectedMsgIds] = useState<Set<string>>(new Set());
  const prevCountRef = useRef(messages.length);
  const msgRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Aire lateral según el ancho REAL del hilo — ver waThreadDensity.
  const densidad = useDensidadPorAncho(scrollContainerRef);

  // Auto-scroll cuando llega un nuevo audit event (pill timeline). El parent
  // (WhatsAppInboxPage) ya scrollea al recibir mensajes, pero los pills vienen
  // del hook `useWaAuditEvents` y nadie los cubría. Usamos misma técnica:
  // detectar incremento del count y disparar scrollIntoView con timeout corto
  // para esperar al commit del DOM.
  const prevAuditCountRef = useRef(auditEvents.length);
  useEffect(() => {
    if (auditEvents.length > prevAuditCountRef.current) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 30);
    }
    prevAuditCountRef.current = auditEvents.length;
  }, [auditEvents.length, messagesEndRef]);

  // Close lightbox / context menu on Escape. (El lightbox tiene su propio
  // ESC handler para navegación, pero centralizamos cierre acá por si
  // se abre con foco fuera.)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (lightboxData) setLightboxData(null);
        if (contextMenuMsgId) setContextMenuMsgId(null);
        if (reactionPickerMsgId) setReactionPickerMsgId(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxData, contextMenuMsgId, reactionPickerMsgId]);

  // Infinite scroll: detect scroll to top
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || !hasOlderMessages || loadingOlder) return;
    if (scrollContainerRef.current.scrollTop < 80) {
      onLoadOlder?.();
    }
  }, [hasOlderMessages, loadingOlder, onLoadOlder]);

  // Si el mensaje aún no está en el DOM al llamar scrollToMessage (p. ej. recién
  // cargado por loadMessagesAround), lo guardamos y un useEffect dispara el
  // scroll cuando el ref aparece tras el commit de React.
  const [pendingScrollId, setPendingScrollId] = useState<string | null>(null);

  const triggerHighlight = useCallback((messageId: string) => {
    setHighlightedMsgId(null);
    requestAnimationFrame(() => {
      setHighlightedMsgId(messageId);
      setTimeout(() => setHighlightedMsgId(null), 2000);
    });
  }, []);

  const scrollToMessage = useCallback((messageId: string) => {
    const el = msgRefs.current.get(messageId);
    if (!el) {
      setPendingScrollId(messageId);
      return;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    triggerHighlight(messageId);
  }, [triggerHighlight]);

  const hasMessage = useCallback((messageId: string) => msgRefs.current.has(messageId), []);

  useImperativeHandle(ref, () => ({ scrollToMessage, hasMessage }), [scrollToMessage, hasMessage]);

  // Resuelve scrolls pendientes cuando los mensajes re-renderizan y el ref
  // del target queda disponible.
  useEffect(() => {
    if (!pendingScrollId) return;
    const el = msgRefs.current.get(pendingScrollId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    triggerHighlight(pendingScrollId);
    setPendingScrollId(null);
  }, [messages, pendingScrollId, triggerHighlight]);

  return (
    <DensidadHiloProvider value={densidad}>
      <style>{`
        @keyframes wa-msg-slide-in {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .wa-msg-new { animation: wa-msg-slide-in 0.25s ease-out; }
      `}</style>
      {/* Pinned message bar */}
      {pinnedMessage ? (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 16px',
            background: '#f0f2f5', borderBottom: '1px solid #e9edef',
            cursor: 'pointer', position: 'relative',
          }}
          onClick={() => scrollToMessage(pinnedMessage.id)}
          onMouseLeave={() => setShowPinMenu(false)}
        >
          <Icon source={PinFilledIcon} tone="subdued" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text as="p" variant="bodySm" truncate>
              {pinnedMessage.body ?? (waMediaPreview(pinnedMessage.msg_type, pinnedMessage.media_caption) || '📎 Archivo')}
            </Text>
          </div>
          {/* Dropdown del pinned message — Polaris Popover + ActionList */}
          <div onClick={(e) => e.stopPropagation()}>
            <Popover
              active={showPinMenu}
              activator={
                <Button
                  variant="plain"
                  icon={ChevronDownIcon}
                  onClick={() => setShowPinMenu(!showPinMenu)}
                  accessibilityLabel="Opciones del mensaje fijado"
                />
              }
              onClose={() => setShowPinMenu(false)}
              preferredAlignment="right"
            >
              <ActionList
                items={[
                  {
                    content: 'Desfijar mensaje',
                    icon: PinIcon,
                    onAction: () => { onUnpinMessage?.(); setShowPinMenu(false); },
                  },
                  {
                    content: 'Ir al mensaje',
                    icon: ArrowDownIcon,
                    onAction: () => { scrollToMessage(pinnedMessage.id); setShowPinMenu(false); },
                  },
                ]}
              />
            </Popover>
          </div>
        </div>
      ) : null}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        style={{
          /*
           * El aire lateral lo decide el ancho REAL de esta caja, no un número fijo. En la columna
           * de la bandeja son 40px y el chat respira; en una ventana acoplada de 340 esos 40 por
           * lado —más la canaleta— se comían el 41% del ancho y estiraban cada mensaje a lo alto.
           */
          flex: 1, overflowY: 'auto', padding: `16px ${densidad.aire}px`,
          display: 'flex', flexDirection: 'column',
        }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><Spinner size="small" /></div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <Icon source={ChatIcon} tone="subdued" />
            <Box paddingBlockStart="200">
              <Text as="p" tone="subdued">Aún no hay mensajes en esta conversación</Text>
            </Box>
          </div>
        ) : (
          <div style={{ marginTop: 'auto' }}>
            {/* Infinite scroll: loading older messages */}
            {loadingOlder ? (
              <Box paddingBlock="300"><InlineStack align="center"><Spinner size="small" /></InlineStack></Box>
            ) : hasOlderMessages ? (
              <Box paddingBlock="200"><InlineStack align="center">
                <Button variant="plain" size="micro" onClick={onLoadOlder}>
                  Cargar mensajes anteriores
                </Button>
              </InlineStack></Box>
            ) : null}
            {(() => {
              // Detectar álbumes de imágenes/videos (≥ ALBUM_MIN_COUNT mensajes
              // consecutivos del mismo sender en <30s). El primer mensaje del
              // grupo renderiza el grid; el resto se omiten del map.
              //
              // Paridad WhatsApp Web:
              //   - 1, 2, 3 imágenes → cada una en su PROPIO bubble (no album)
              //   - 4+ imágenes      → colage 2×2 con overlay "+N" si hay más
              const ALBUM_MIN_COUNT = 4;
              const albumStarts = new Map<string, WaMessage[]>();
              const albumMembers = new Set<string>();
              for (let i = 0; i < messages.length; ) {
                const m = messages[i];
                if (!isAlbumCandidate(m)) { i++; continue; }
                const group = [m];
                let j = i + 1;
                while (j < messages.length && canExtendAlbum(group[group.length - 1], messages[j])) {
                  group.push(messages[j]);
                  j++;
                }
                if (group.length >= ALBUM_MIN_COUNT) {
                  albumStarts.set(m.id, group);
                  for (const g of group) albumMembers.add(g.id);
                }
                i = j;
              }

              // ─── Burst groups: avatar sticky por racha (paridad WA Web) ──
              // Cuando un participante manda varios mensajes seguidos, el
              // avatar de su foto se "mantiene fijo" en la columna izquierda
              // mientras toda la racha es scrolleada — como WhatsApp Web.
              //
              // Agrupamos rachas de 2+ mensajes consecutivos del mismo
              // participante en el mismo día y las renderizamos dentro de
              // un BurstContainer: flex row con un único avatar
              // `position: sticky; top: 12px` a la izquierda y la columna
              // de bubbles a la derecha.
              //
              // Reglas:
              //   - Solo cuenta inbound de grupo (msg.group_participant_name)
              //   - Outbound/system rompen la racha
              //   - is_internal_note se ignora (layer visual aparte)
              //   - Cambio de día rompe la racha (date pill separa)
              //   - Burst requiere >= 2 mensajes; single msgs renderizan
              //     normalmente con su propio avatar (no necesitan sticky)
              //   - Anchor de album cuenta como 1 render unit; sus miembros
              //     internos NO (no renderizan individualmente)
              const isRenderUnit = (m: WaMessage): boolean =>
                !(albumMembers.has(m.id) && !albumStarts.has(m.id));

              interface BurstGroup { anchorId: string; msgIds: string[]; }
              const burstByAnchor = new Map<string, BurstGroup>();
              const msgIdToBurstAnchor = new Map<string, string>();
              {
                let current: BurstGroup | null = null;
                let lastSenderKey: string | null = null;
                let lastDateString: string | null = null;
                const flush = () => {
                  if (current && current.msgIds.length >= 2) {
                    burstByAnchor.set(current.anchorId, current);
                    for (const id of current.msgIds) {
                      msgIdToBurstAnchor.set(id, current.anchorId);
                    }
                  }
                  current = null;
                };
                for (const m of messages) {
                  if (!isRenderUnit(m)) continue;
                  if (m.is_internal_note) continue;
                  if (
                    m.direction !== 'inbound'
                    || m.sender_type === 'system'
                    || !m.group_participant_name
                  ) {
                    flush();
                    lastSenderKey = null;
                    lastDateString = null;
                    continue;
                  }
                  const dateString = new Date(m.created_at).toDateString();
                  const senderKey = m.group_participant_phone
                    || m.group_participant_jid
                    || m.group_participant_name;
                  if (
                    current
                    && lastSenderKey === senderKey
                    && lastDateString === dateString
                  ) {
                    current.msgIds.push(m.id);
                  } else {
                    flush();
                    current = { anchorId: m.id, msgIds: [m.id] };
                  }
                  lastSenderKey = senderKey;
                  lastDateString = dateString;
                }
                flush();
              }
              // Renderer compartido: una fila individual DENTRO del
               // BurstContainer (no fuera). Recibe el msg correspondiente
               // y los flags pre-computados de su estado reactivo.
               // omitParticipantAvatarColumn=true porque el avatar lo dibuja
               // el BurstContainer una sola vez con position: sticky.
               const renderRowInBurst = (bmsg: WaMessage): React.ReactNode => (
                 <WaMessageRow
                   key={bmsg.id}
                   msg={bmsg}
                   isNew={false}
                   showDate={false}
                   pills={undefined}
                   isHovered={hoveredMsgId === bmsg.id}
                   isMenuOpen={contextMenuMsgId === bmsg.id}
                   menuOpenUp={contextMenuOpenUp}
                   isReactionPickerOpen={reactionPickerMsgId === bmsg.id}
                   isHighlighted={highlightedMsgId === bmsg.id}
                   isRetrying={retryingMsgIds.has(bmsg.id)}
                   retryFailed={failedRetryMsgIds.has(bmsg.id)}
                   isSelected={selectMode && selectedMsgIds.has(bmsg.id)}
                   isPinned={pinnedMessage?.id === bmsg.id}
                   selectMode={selectMode}
                   contactPhotoUrl={contactPhotoUrl}
                   currentUserAvatarUrl={currentUserAvatarUrl}
                   mentionableNames={mentionableNames}
                   uniqueAgentCount={uniqueAgentCount}
                   groupParticipantAvatars={groupParticipantAvatars}
                   groupMentionMap={groupMentionMap}
                   omitParticipantAvatarColumn
                   msgRef={(el) => { if (el) msgRefs.current.set(bmsg.id, el); }}
                   scrollContainerRef={scrollContainerRef}
                   messagesEndRef={messagesEndRef}
                   onSendReaction={onSendReaction}
                   onRetryMessage={onRetryMessage}
                   onForwardMessage={onForwardMessage}
                   onDeleteMessage={onDeleteMessage}
                   onReplyMessage={onReplyMessage}
                   onEditMessage={onEditMessage}
                   onShowMessageInfo={onShowMessageInfo}
                   onUnpinMessage={onUnpinMessage}
                   onStarMessage={onStarMessage}
                   onShowOrderDetails={(params) => setOrderDetailsCtx(params)}
                   onSendQuickReply={onSendQuickReply}
                   conversationId={conversationId ?? undefined}
                   resolveAgentName={resolveAgentName}
                   retryMediaDownload={retryMediaDownload}
                   onHoverStart={() => setHoveredMsgId(bmsg.id)}
                   onHoverEnd={() => { setHoveredMsgId(null); setContextMenuMsgId((prev) => prev === bmsg.id ? null : prev); }}
                   onMenuOpen={(openUp) => { setContextMenuOpenUp(openUp); setContextMenuMsgId(bmsg.id); setReactionPickerMsgId(null); }}
                   onMenuClose={() => setContextMenuMsgId(null)}
                   onReactionPickerToggle={() => setReactionPickerMsgId(reactionPickerMsgId === bmsg.id ? null : bmsg.id)}
                   onReactionPickerClose={() => setReactionPickerMsgId(null)}
                   onLightboxOpen={(url) => openLightboxSingle(url, bmsg)}
                   onPinModalOpen={() => setPinModalMsg(bmsg)}
                   onReactionDetailOpen={() => setReactionDetailMsgId(bmsg.id)}
                   onSelectToggle={() => setSelectedMsgIds((prev) => { const next = new Set(prev); if (next.has(bmsg.id)) next.delete(bmsg.id); else next.add(bmsg.id); return next; })}
                   onSelectModeStart={() => { setSelectMode(true); setSelectedMsgIds(new Set([bmsg.id])); setContextMenuMsgId(null); }}
                   onRetryTarget={() => setRetryTarget(bmsg)}
                 />
               );

              return messages.map((msg, idx) => {
              // Miembros del álbum (excepto el primero) se renderizan dentro
              // del grid del primer mensaje; aquí los omitimos.
              if (albumMembers.has(msg.id) && !albumStarts.has(msg.id)) return null;

              // Miembros del burst (excepto el anchor) se renderizan dentro
              // del BurstContainer del anchor; aquí los omitimos.
              const burstAnchorIdForMsg = msgIdToBurstAnchor.get(msg.id);
              if (burstAnchorIdForMsg !== undefined && burstAnchorIdForMsg !== msg.id) return null;

              const isNew = idx >= prevCountRef.current;
              if (idx === messages.length - 1) prevCountRef.current = messages.length;
              const showDate = idx === 0 || (
                new Date(msg.created_at).toDateString() !== new Date(messages[idx - 1].created_at).toDateString()
              );
              /*
               * ¿Cambió quién habla respecto del mensaje anterior? WhatsApp separa poco los
               * mensajes seguidos de la misma persona —se leen como una parrafada— y deja más aire
               * cuando cambia el turno, que es lo que hace legible una conversación de un vistazo.
               * En un grupo, "quién habla" incluye al participante, no solo el lado del globo.
               */
              const anterior = messages[idx - 1];
              const autorDe = (m: typeof msg | undefined) => (m
                ? `${m.direction}|${m.is_internal_note ? 'nota' : ''}|${m.group_participant_phone ?? ''}`
                : '');
              const cambioDeAutor = idx === 0 || autorDe(anterior) !== autorDe(msg);

              const isSelected = selectMode && selectedMsgIds.has(msg.id);
              const pills = eventGroups.groups.get(msg.id);
              const pillsNode = pills?.map((e) => (
                <WaTimelinePill key={e.id} event={e} resolveName={resolveAgentName} />
              ));

              // ─── Burst anchor: render BurstContainer con avatar sticky ──
              const burst = burstByAnchor.get(msg.id);
              if (burst) {
                const cacheKey = deriveParticipantAvatarKey(msg.group_participant_phone, msg.group_participant_jid);
                const avatarUrl = cacheKey ? groupParticipantAvatars?.[cacheKey] : null;
                const participantBg = groupParticipantColor(cacheKey ?? msg.group_participant_name ?? '');
                const burstMsgs = burst.msgIds.map((id) => messages.find((m) => m.id === id)!);
                return (
                  <div key={`burst-${msg.id}`} className={isNew ? 'wa-msg-new' : undefined}>
                    {pillsNode}
                    {showDate ? (
                      <Box paddingBlockStart="400" paddingBlockEnd="300">
                        <InlineStack align="center">
                          <span style={{
                            background: 'var(--p-color-bg-surface)',
                            border: '1px solid var(--p-color-border-subdued)',
                            borderRadius: 8, padding: '4px 14px',
                            fontSize: 12, color: 'var(--p-color-text-subdued)',
                          }}>
                            {new Date(msg.created_at).toLocaleDateString('es-PE', { weekday: 'long', day: 'numeric', month: 'long' })}
                          </span>
                        </InlineStack>
                      </Box>
                    ) : null}
                    <div
                      data-msg-id={msg.id}
                      ref={(el) => { if (el) msgRefs.current.set(msg.id, el); }}
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        gap: 8,
                        paddingLeft: 0,
                        paddingRight: densidad.canaleta,
                      }}
                    >
                      {/* Avatar sticky: se queda visible mientras el burst
                          completo es scrolleado. position: sticky se
                          ancla al scroll container más cercano (el thread). */}
                      <div style={{
                        flexShrink: 0,
                        position: 'sticky',
                        top: 12,
                        alignSelf: 'flex-start',
                      }}>
                        <WaAvatar
                          size={32}
                          src={avatarUrl}
                          initials={groupParticipantInitials(msg.group_participant_name ?? '')}
                          bg={participantBg}
                          fontSize={13}
                          alt={msg.group_participant_name ?? ''}
                        />
                      </div>
                      {/* Columna de bubbles del burst */}
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        {burstMsgs.map((bmsg) => {
                          // Album anchor dentro de burst: render el album
                          // directo (sin su wrapper de avatar), con el
                          // nombre del participante arriba.
                          const bmsgAlbum = albumStarts.get(bmsg.id);
                          if (bmsgAlbum) {
                            return (
                              <div
                                key={bmsg.id}
                                data-msg-id={bmsg.id}
                                ref={(el) => { if (el) msgRefs.current.set(bmsg.id, el); }}
                                style={{ padding: '2px 0' }}
                              >
                                <div style={{
                                  fontSize: 13, fontWeight: 600,
                                  color: participantBg,
                                  marginBottom: 4, whiteSpace: 'nowrap',
                                  overflow: 'hidden', textOverflow: 'ellipsis',
                                  maxWidth: 280,
                                }}>
                                  {bmsg.group_participant_name}
                                  {bmsg.group_participant_phone ? (
                                    <span style={{ fontWeight: 400, opacity: 0.7, marginLeft: 6, fontSize: 12 }}>
                                      {bmsg.group_participant_phone}
                                    </span>
                                  ) : null}
                                </div>
                                <WaImageAlbum
                                  msgs={bmsgAlbum}
                                  isOutbound={false}
                                  isNote={bmsg.is_internal_note}
                                  onLightboxOpen={openLightboxForMessages}
                                />
                              </div>
                            );
                          }
                          return renderRowInBurst(bmsg);
                        })}
                      </div>
                    </div>
                  </div>
                );
              }

              // Render álbum si este es el primer mensaje del grupo.
              const albumGroup = albumStarts.get(msg.id);
              if (albumGroup) {
                const isOutbound = msg.direction === 'outbound';
                return (
                  <div key={msg.id} className={isNew ? 'wa-msg-new' : undefined}>
                    {pillsNode}
                    {showDate ? (
                      <Box paddingBlockStart="400" paddingBlockEnd="300">
                        <InlineStack align="center">
                          <span style={{
                            background: 'var(--p-color-bg-surface)',
                            border: '1px solid var(--p-color-border-subdued)',
                            borderRadius: 8, padding: '4px 14px',
                            fontSize: 12, color: 'var(--p-color-text-subdued)',
                          }}>
                            {new Date(msg.created_at).toLocaleDateString('es-PE', { weekday: 'long', day: 'numeric', month: 'long' })}
                          </span>
                        </InlineStack>
                      </Box>
                    ) : null}
                    {/* Paridad WaMessageRow para inbound de grupo: avatar
                        lateral 32px del participante + nombre coloreado arriba
                        del album. Sin esto el bubble del colage quedaba flush
                        a la izquierda mientras los otros mensajes del mismo
                        sender tenían sangría — feo y desalineado. */}
                    {(() => {
                      const isGroupInbound = !isOutbound && !msg.is_internal_note && !!msg.group_participant_name;
                      const cacheKey = isGroupInbound
                        ? deriveParticipantAvatarKey(msg.group_participant_phone, msg.group_participant_jid)
                        : null;
                      const avatarUrl = cacheKey ? groupParticipantAvatars?.[cacheKey] : null;
                      const participantBg = isGroupInbound
                        ? groupParticipantColor(cacheKey ?? msg.group_participant_name ?? '')
                        : '';
                      // Albums standalone (no en burst) siempre muestran su avatar.
                      // Albums dentro de burst no llegan acá — ya se renderizaron
                      // dentro del BurstContainer arriba.
                      return (
                        <div
                          data-msg-id={msg.id}
                          ref={(el) => { if (el) msgRefs.current.set(msg.id, el); }}
                          style={{
                            display: 'flex',
                            justifyContent: isOutbound ? 'flex-end' : 'flex-start',
                            alignItems: 'flex-end',
                            gap: 8,
                            // Mismo criterio que WaMessageRow: pegado al anterior si es del mismo
                            // autor, con aire si cambió el turno.
                            paddingTop: cambioDeAutor ? densidad.saltoOtroAutor : densidad.saltoMismoAutor,
                            paddingBottom: densidad.saltoMismoAutor,
                            paddingLeft: isOutbound ? densidad.canaleta : 0,
                            paddingRight: isOutbound ? 0 : densidad.canaleta,
                          }}
                        >
                          {/* Avatar lateral del participante (idéntico a
                              WaMessageRow para que el album quede alineado
                              con el resto de los mensajes del thread).
                              Si el album es continuación de una racha del
                              mismo participante, spacer en vez de avatar. */}
                          {isGroupInbound ? (
                            <WaAvatar
                              size={32}
                              src={avatarUrl}
                              initials={groupParticipantInitials(msg.group_participant_name ?? '')}
                              bg={participantBg}
                              fontSize={13}
                              alt={msg.group_participant_name ?? ''}
                            />
                          ) : null}
                          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            {isGroupInbound ? (
                              <div style={{
                                fontSize: 13, fontWeight: 600,
                                color: groupParticipantColor(msg.group_participant_phone ?? msg.group_participant_name ?? ''),
                                marginBottom: 4, whiteSpace: 'nowrap',
                                overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 280,
                              }}>
                                {msg.group_participant_name}
                                {msg.group_participant_phone ? (
                                  <span style={{ fontWeight: 400, opacity: 0.7, marginLeft: 6, fontSize: 12 }}>
                                    {msg.group_participant_phone}
                                  </span>
                                ) : null}
                              </div>
                            ) : null}
                            <WaImageAlbum
                              msgs={albumGroup}
                              isOutbound={isOutbound}
                              isNote={msg.is_internal_note}
                              onLightboxOpen={openLightboxForMessages}
                            />
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              }

              return (
                <WaMessageRow
                  key={msg.id}
                  msg={msg}
                  isNew={isNew}
                  showDate={showDate}
                  cambioDeAutor={cambioDeAutor}
                  pills={pillsNode}
                  isHovered={hoveredMsgId === msg.id}
                  isMenuOpen={contextMenuMsgId === msg.id}
                  menuOpenUp={contextMenuOpenUp}
                  isReactionPickerOpen={reactionPickerMsgId === msg.id}
                  isHighlighted={highlightedMsgId === msg.id}
                  isRetrying={retryingMsgIds.has(msg.id)}
                  retryFailed={failedRetryMsgIds.has(msg.id)}
                  isSelected={isSelected}
                  isPinned={pinnedMessage?.id === msg.id}
                  selectMode={selectMode}
                  contactPhotoUrl={contactPhotoUrl}
                  currentUserAvatarUrl={currentUserAvatarUrl}
                  mentionableNames={mentionableNames}
                  uniqueAgentCount={uniqueAgentCount}
                  groupParticipantAvatars={groupParticipantAvatars}
                  groupMentionMap={groupMentionMap}
                  msgRef={(el) => { if (el) msgRefs.current.set(msg.id, el); }}
                  scrollContainerRef={scrollContainerRef}
                  messagesEndRef={messagesEndRef}
                  onSendReaction={onSendReaction}
                  onRetryMessage={onRetryMessage}
                  onForwardMessage={onForwardMessage}
                  onDeleteMessage={onDeleteMessage}
                  onReplyMessage={onReplyMessage}
                  onEditMessage={onEditMessage}
                  onShowMessageInfo={onShowMessageInfo}
                  onUnpinMessage={onUnpinMessage}
                  onStarMessage={onStarMessage}
                  onShowOrderDetails={(params) => setOrderDetailsCtx(params)}
                  onSendQuickReply={onSendQuickReply}
                  conversationId={conversationId ?? undefined}
                  resolveAgentName={resolveAgentName}
                  retryMediaDownload={retryMediaDownload}
                  onHoverStart={() => setHoveredMsgId(msg.id)}
                  onHoverEnd={() => { setHoveredMsgId(null); setContextMenuMsgId((prev) => prev === msg.id ? null : prev); }}
                  onMenuOpen={(openUp) => { setContextMenuOpenUp(openUp); setContextMenuMsgId(msg.id); setReactionPickerMsgId(null); }}
                  onMenuClose={() => setContextMenuMsgId(null)}
                  onReactionPickerToggle={() => setReactionPickerMsgId(reactionPickerMsgId === msg.id ? null : msg.id)}
                  onReactionPickerClose={() => setReactionPickerMsgId(null)}
                  onLightboxOpen={(url) => openLightboxSingle(url, msg)}
                  onPinModalOpen={() => setPinModalMsg(msg)}
                  onReactionDetailOpen={() => setReactionDetailMsgId(msg.id)}
                  onSelectToggle={() => setSelectedMsgIds((prev) => { const next = new Set(prev); if (next.has(msg.id)) next.delete(msg.id); else next.add(msg.id); return next; })}
                  onSelectModeStart={() => { setSelectMode(true); setSelectedMsgIds(new Set([msg.id])); setContextMenuMsgId(null); }}
                  onRetryTarget={() => setRetryTarget(msg)}
                />
              );
              });
            })()}
            {eventGroups.tail.map((e) => (
              <WaTimelinePill key={e.id} event={e} resolveName={resolveAgentName} />
            ))}
            <div ref={messagesEndRef as React.RefObject<HTMLDivElement>} />
          </div>
        )}
      </div>

      {/* Barra inferior en modo selección */}
      {selectMode && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 16px',
          background: 'var(--p-color-bg-surface)',
          borderTop: '1px solid var(--p-color-border)',
        }}>
          <span style={{ fontSize: 14, color: 'var(--p-color-text-subdued)' }}>
            {selectedMsgIds.size} mensaje{selectedMsgIds.size !== 1 ? 's' : ''} seleccionado{selectedMsgIds.size !== 1 ? 's' : ''}
          </span>
          <InlineStack gap="200">
            <Button onClick={() => { setSelectMode(false); setSelectedMsgIds(new Set()); }}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              disabled={selectedMsgIds.size === 0}
              onClick={() => {
                const selectedMsgs = messages
                  .filter((m) => selectedMsgIds.has(m.id))
                  .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
                if (selectedMsgs.length === 1 && onForwardMessage) {
                  onForwardMessage(selectedMsgs[0]);
                } else if (selectedMsgs.length > 0 && onForwardMessages) {
                  onForwardMessages(selectedMsgs);
                } else if (selectedMsgs.length > 0 && onForwardMessage) {
                  onForwardMessage(selectedMsgs[0]);
                }
                setSelectMode(false);
                setSelectedMsgIds(new Set());
              }}
            >
              Reenviar
            </Button>
          </InlineStack>
        </div>
      )}

      {/* Reaction detail modal */}
      {reactionDetailMsgId ? (
        <WaReactionDetailModal
          messages={messages}
          msgId={reactionDetailMsgId}
          onRemoveOwnReaction={() => onSendReaction(reactionDetailMsgId, '')}
          onClose={() => setReactionDetailMsgId(null)}
        />
      ) : null}

      {/* Pin message modal — WhatsApp Web style */}
      {pinModalMsg ? (
        <WaPinModal
          msg={pinModalMsg}
          onPin={(msg, duration) => onPinMessage?.(msg, duration)}
          onClose={() => setPinModalMsg(null)}
        />
      ) : null}

      {/* Feature 5c-B: Order details modal — items individuales del orderMessage. */}
      {orderDetailsCtx && conversationId ? (
        <WaOrderDetailsModal
          isOpen
          onClose={() => setOrderDetailsCtx(null)}
          conversationId={conversationId}
          orderId={orderDetailsCtx.orderId}
          orderToken={orderDetailsCtx.token}
          totalAmount1000={orderDetailsCtx.totalAmount1000}
          currency={orderDetailsCtx.currency}
          orderDate={orderDetailsCtx.orderDate}
          prefetchedItems={orderDetailsCtx.prefetchedItems}
          customerName={contactName}
          customerPhone={contactPhone}
          customerPhotoUrl={contactPhotoUrl}
          onCreateInternalOrder={onCreateOrderFromCatalog
            ? (params) => {
                onCreateOrderFromCatalog(params);
                setOrderDetailsCtx(null);
              }
            : undefined}
          onSendQuickReply={onSendQuickReply}
        />
      ) : null}

      {/* Image lightbox — fullscreen modal like WhatsApp Web. Carrusel
          completo con header, navegación, strip de miniaturas y todas
          las acciones (star, pin, reply, forward, react, download). */}
      {lightboxData && lightboxMsgs && lightboxMsgs.length > 0 ? (
        <WaImageLightbox
          msgs={lightboxMsgs}
          initialIndex={lightboxData.index}
          contactPhotoUrl={contactPhotoUrl}
          currentUserAvatarUrl={currentUserAvatarUrl}
          pinnedMessageId={pinnedMessage?.id ?? null}
          onClose={() => setLightboxData(null)}
          onStarMessage={onStarMessage}
          onRequestPin={(m) => { setPinModalMsg(m); setLightboxData(null); }}
          onUnpinMessage={onUnpinMessage}
          onReplyMessage={(m) => { onReplyMessage?.(m); setLightboxData(null); }}
          onForwardMessage={(m) => { onForwardMessage?.(m); setLightboxData(null); }}
          onSendReaction={onSendReaction}
          onGoToMessage={(id) => { setLightboxData(null); scrollToMessage(id); }}
        />
      ) : null}

      {/* Modal "No se envió tu mensaje" — patrón WhatsApp Web. Se abre al
          hacer click en el icono rojo de un mensaje fallido. El error técnico
          (si vino del edge function) se muestra como descripción. */}
      <AppConfirmModal
        open={!!retryTarget}
        onClose={() => setRetryTarget(null)}
        title="No se envió tu mensaje"
        message="No pudimos enviarlo. ¿Quieres volver a intentarlo?"
        description={retryTarget?.error_message ?? undefined}
        confirmLabel="Volver a intentarlo"
        onConfirm={() => {
          if (retryTarget && onRetryMessage) {
            onRetryMessage(retryTarget.id);
          }
          setRetryTarget(null);
        }}
      />
    </DensidadHiloProvider>
  );
});
